import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Descriptions, Tag, message, Card, Spin, Space, Popconfirm,
  Modal, Form, Input, Select, Tabs, Radio, InputNumber, Upload,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, DeleteOutlined, RobotOutlined, UploadOutlined,
} from '@ant-design/icons';
import { documentsApi, dictionariesApi } from '../services/api-services';
import { useStreamGeneration } from '../hooks/useStreamGeneration';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-markdown-editor-lite/lib/index.css';
import '../styles/markdown.css';

const { TextArea } = Input;
const mdParser = new MarkdownIt();

const DocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { generating: streamGenerating, generate: streamGenerate, cancel: streamCancel } = useStreamGeneration();
  const [doc, setDoc] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [generateMode, setGenerateMode] = useState<'full' | 'range'>('full');
  const [rangeStart, setRangeStart] = useState<number>(0);
  const [rangeEnd, setRangeEnd] = useState<number>(1000);
  const [editUploadedPath, setEditUploadedPath] = useState('');
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isFullscreen = !!document.querySelector('.rc-md-editor.full');
      document.documentElement.classList.toggle('md-fullscreen', isFullscreen);
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      document.documentElement.classList.remove('md-fullscreen');
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await documentsApi.get(Number(id));
      setDoc(res);
    } catch {
      message.error('获取文档详情失败');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
    dictionariesApi.getByCode('resource_tag').then((dict: any) => {
      if (dict?.items) setTagOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    }).catch(() => {});
  }, [id, fetchData]);

  const handleSaveContent = async () => {
    try {
      // 优先使用 AI 生成的描述
      let description = (window as any).__tempDocDesc || doc.description;
      
      // 如果没有 AI 描述，尝试从 Markdown 提取
      if (!((window as any).__tempDocDesc)) {
        const overviewMatch = editedContent.match(/##?\s*(概述|摘要|简介|Overview|Summary)\s*\n+([\s\S]*?)(?=\n##|$)/i);
        if (overviewMatch) {
          const overview = overviewMatch[2].trim();
          description = overview.length > 200 ? overview.substring(0, 200) + '...' : overview;
        }
      }
      
      await documentsApi.update(Number(id), { 
        content: editedContent,
        description: description
      });
      
      // 清除临时存储
      delete (window as any).__tempDocDesc;
      
      message.success('文档说明保存成功');
      setIsEditingContent(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '保存失败');
    }
  };

  const handleGenerate = () => {
    console.log('[DocumentDetail] handleGenerate called');
    setGenerateModalVisible(false);
    // 退出编辑模式
    setIsEditingContent(false);
    const mode = generateMode === 'range' ? 'partial' : 'full';
    const options = generateMode === 'range' ? { range_start: rangeStart, range_end: rangeEnd } : {};
    
    console.log('[DocumentDetail] About to call streamGenerate with:', { mode, options });
    let collectedContent = '';
    
    streamGenerate({
      action: 'create_task',
      payload: { 
        key: 'generate_document',
        id: Number(id),
        mode,
        options,
        resourceName: `${doc?.title || '文档'} - 说明生成`,
      },
      navigationUrl: `/documents/${id}`,
      onChunk: (chunk: string) => {
        collectedContent += chunk;
      },
      onDone: async () => {
        // 提取概述部分作为描述
        try {
          let description = doc.description;
          
          // 尝试提取 "## 概述" 部分
          const overviewMatch = collectedContent.match(/##\s*概述\s*\n([\s\S]*?)(?=\n##|$)/);
          if (overviewMatch && overviewMatch[1]) {
            description = overviewMatch[1].trim();
          }
          
          await documentsApi.update(Number(id), { 
            content: collectedContent,
            description: description
          });
          fetchData();
        } catch (e: any) {
          message.error(e.response?.data?.detail || '保存失败');
        }
      },
      onError: (error: string) => {
        message.error(error || '生成失败');
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openEditModal = () => {
    editForm.setFieldsValue({
      identifier: doc.identifier,
      title: doc.title,
      description: doc.description || '',
      tags: doc.tags || [],
    });
    setEditUploadedPath('');
    setEditModalVisible(true);
  };

  const handleEditSave = async (values: any) => {
    try {
      const payload: any = { ...values };
      if (editUploadedPath) {
        payload.file_path = editUploadedPath;
      }
      await documentsApi.update(Number(id), payload);
      message.success('更新成功');
      setEditModalVisible(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '更新失败');
    }
  };

  const handleDelete = async () => {
    try {
      await documentsApi.delete(Number(id));
      message.success('删除成功');
      navigate('/documents');
    } catch {
      message.error('删除失败');
    }
  };

  if (loading && !doc) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="apple-page">
        <div className="apple-empty">
          <p className="apple-empty-title">未找到文档</p>
          <Button type="primary" onClick={() => navigate('/documents')}>返回列表</Button>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: '基础信息',
      children: (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="标识">{doc.identifier}</Descriptions.Item>
          <Descriptions.Item label="名称">
            <span style={{ whiteSpace: 'nowrap' }}>{doc.title}</span>
          </Descriptions.Item>
          <Descriptions.Item label="描述">{doc.description || '-'}</Descriptions.Item>
          <Descriptions.Item label="文档路径">
            {doc.file_path ? (
              <a 
                href={doc.file_path.startsWith('/') ? doc.file_path : `/${doc.file_path}`}
                download={doc.file_path.split('/').pop()}
                style={{ color: '#1890ff', cursor: 'pointer' }}
                title="点击下载"
              >
                {doc.file_path.split('/').pop()}
              </a>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="标签">
            {doc.tags?.map((t: string) => <Tag key={t} style={{ margin: '2px 4px 2px 0' }}>{t}</Tag>) || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{doc.created_at ? new Date(doc.created_at).toLocaleString() : '-'}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{doc.updated_at ? new Date(doc.updated_at).toLocaleString() : '-'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'content',
      label: '文档说明',
      children: (
        <Card
          title="文档说明"
          styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
          extra={
            isEditingContent ? (
              <Space>
                <Button
                  icon={<RobotOutlined />}
                  loading={streamGenerating}
                  onClick={() => !streamGenerating && setGenerateModalVisible(true)}
                  disabled={streamGenerating}
                >
                  {streamGenerating ? '生成中...' : 'AI 生成'}
                </Button>
                <Button onClick={() => {
                  if (streamGenerating) {
                    streamCancel();
                  }
                  setIsEditingContent(false);
                }}>取消</Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveContent}>保存</Button>
              </Space>
            ) : (
              <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedContent(doc.content || ''); setIsEditingContent(true); }} />
            )
          }
        >
          {isEditingContent ? (
            <MdEditor
              value={editedContent}
              style={{ height: '500px' }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setEditedContent(text)}
              config={{
                view: { menu: true, md: true, html: true },
                canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
              }}
            />
          ) : (
            <div className="markdown-preview">
              {doc.content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown> : <p style={{ color: 'var(--apple-text-secondary)', padding: 24 }}>暂无文档说明，点击「AI 生成」自动分析生成</p>}
            </div>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/documents')} />
          <h1 className="apple-page-title" style={{ marginBottom: 0 }}>{doc.title}</h1>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={openEditModal}>编辑</Button>
          <Popconfirm title="确认删除" description="确定要删除此文档吗？" onConfirm={handleDelete} okText="确定" cancelText="取消">
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      </div>

      <Tabs items={tabItems} />

      <Modal
        className="apple-modal"
        title="编辑文档"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        destroyOnHidden
      >
        <Form className="apple-form" form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="identifier" label="标识" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: '仅支持小写字母、数字和连字符' }, { max: 64, message: '最长64字符' }]}><Input /></Form.Item>
          <Form.Item name="title" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><TextArea rows={3} /></Form.Item>
          <Form.Item label="文档路径">
            <div style={{ marginBottom: 8, color: 'var(--apple-text-secondary)', fontSize: 12 }}>
              当前：{doc.file_path ? doc.file_path.replace(/^.*?\/backend\//, '') : '无'}
            </div>
            <Upload
              action={`${process.env.REACT_APP_API_URL || '/api/v1'}/documents/upload-file`}
              headers={{ Authorization: `Bearer ${localStorage.getItem('token')}` }}
              maxCount={1}
              showUploadList={true}
              onChange={(info) => {
                if (info.file.status === 'done') {
                  setEditUploadedPath(info.file.response?.file_path || '');
                  message.success('文件上传成功');
                } else if (info.file.status === 'error') {
                  message.error('文件上传失败');
                }
              }}
            >
              <Button icon={<UploadOutlined />}>重新上传</Button>
            </Upload>
            {editUploadedPath && <div style={{ marginTop: 8, color: 'var(--apple-text-secondary)', fontSize: 12 }}>新文件：{editUploadedPath}</div>}
          </Form.Item>
          <Form.Item name="tags" label="标签"><Select mode="multiple" placeholder="选择标签" options={tagOptions} allowClear /></Form.Item>
        </Form>
      </Modal>

      <Modal
        className="apple-modal"
        title="AI 生成文档说明"
        open={generateModalVisible}
        onCancel={() => setGenerateModalVisible(false)}
        onOk={handleGenerate}
        okText="开始生成"
        cancelText="取消"
        destroyOnHidden
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group value={generateMode} onChange={(e) => setGenerateMode(e.target.value)}>
            <Radio value="full">全量生成</Radio>
            <Radio value="range">按字数读取</Radio>
          </Radio.Group>
        </div>
        {generateMode === 'full' && (
          <p style={{ color: 'var(--apple-text-secondary)' }}>
            将读取文档全部内容，生成包含目录结构和内容索引的完整说明。
          </p>
        )}
        {generateMode === 'range' && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <p style={{ color: 'var(--apple-text-secondary)' }}>
              指定字符范围，仅对该范围内的内容生成说明。
            </p>
            <Space>
              <InputNumber
                min={0}
                value={rangeStart}
                onChange={(v) => setRangeStart(v || 0)}
                addonBefore="起始"
                style={{ width: 160 }}
              />
              <InputNumber
                min={0}
                value={rangeEnd}
                onChange={(v) => setRangeEnd(v || 0)}
                addonBefore="结束"
                style={{ width: 160 }}
              />
            </Space>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default DocumentDetail;
